import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';

import { CHANNEL, colorPageSlugs } from '@boss/constants/b2b-b2c';
import { getEntryByTag } from '@boss/contentful-client/b2b-b2c';
import { color as colorService, mapNextToContentfulMarket } from '@boss/services';
import { Environment, SectionFields } from '@boss/types/b2b-b2c';
import { getTranslatedSlugsByType } from '@boss/utils';

import { SEO } from '../../components';
import { PageTypes } from '../../constants';
import ColorDetailPage from '../../containers/colorDetailPage';
import { useRecentViewedColors } from '../../hooks';
import Layout from '../../layout/default';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapNextToContentfulLocaleMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';

export const config = {
  maxDuration: 30,
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({ preview, locale, params }: GetStaticPropsContext) => {
  const slug = params?.slug;

  if (!slug || Array.isArray(slug) || !locale || locale === 'default') {
    return {
      notFound: true,
    };
  }

  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);
  const market = mapNextToContentfulMarket(locale);

  const [navigation, color, ctaSection] = await Promise.all([
    getMappedNavigation(contentfulLocaleMarket, !!preview),
    colorService.getBySlug(slug, contentfulLocaleMarket, CHANNEL, market),
    getEntryByTag<SectionFields>('displayColorPageCtas', contentfulLocaleMarket),
  ]);

  if (!color) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      pageType: PageTypes.COLOR_DETAIL,
      color,
      locale,
      preview: !!preview,
      navigation,
      ctaSection,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      ...(await getServerSideTranslations(locale, ['common', 'color', 'product', 'forms', 'colorTool'])),
    },
  };
};

export const ColorDetail = ({
  navigation,
  preview,
  locale,
  environment,
  color,
  ctaSection,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { addColor } = useRecentViewedColors();
  const { t } = useTranslation();
  const pageTitle = color.name;
  const pageDescription = t('meta.description', { ns: 'color', colorName: color.name });
  const localizedSlugs = getTranslatedSlugsByType(color.slug, 'Color overview');

  const crumbles = [
    { href: colorPageSlugs[locale], label: t('colorLabel', { ns: 'common' }) },
    { href: color.slug, label: pageTitle },
  ];

  useEffect(() => {
    addColor(color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color]);

  return (
    <SEO
      breadcrumbs={crumbles}
      description={color.seoDescription ?? pageDescription}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      title={color.seoTitle ?? `${color.code} ${color.name}`}
    >
      <Layout
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={localizedSlugs}
        navigation={navigation}
        preview={preview}
      >
        <ColorDetailPage color={color} ctaSection={ctaSection} locale={locale} />
      </Layout>
    </SEO>
  );
};

export default ColorDetail;
